import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Building2, Phone, ArrowRight, Loader2, Sparkles, CheckCircle, Shield, BarChart3, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { AuthError } from '../components/AuthError';
import { authErrorMessage, companyCodeInput } from '../utils/authForm';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({ companyCode: '', phone: '' });
  const [focusedField, setFocusedField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage('');
    if (!formData.companyCode.trim() || !formData.phone.trim()) {
      setErrorMessage('Please enter company code and phone number');
      return;
    }
    setLoading(true);
    try {
      const result = await login(formData.companyCode, formData.phone);
      toast.success('Welcome back!');
      const userData = result?.user || {};
      navigate(userData.is_ceo ? '/ceo' : '/dashboard');
    } catch (err) {
      setErrorMessage(authErrorMessage(err, 'Login failed'));
    } finally { setLoading(false); }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT (Centered, Single Column)
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div data-testid="login-page" style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Manrope', 'Inter', sans-serif",
        background: 'linear-gradient(165deg, #f0fdfa 0%, #ecfdf5 30%, #f0fdf4 60%, #faf5ff 100%)',
        position: 'relative',
        overflowX: 'hidden', overflowY: 'auto',
      }}>
        
        {/* Animated gradient orbs */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(20,184,166,0.15) 0%, transparent 70%)',
            top: -100, right: 0, animation: 'float 8s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', width: 250, height: 250, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
            bottom: -50, left: -60, animation: 'float 10s ease-in-out infinite reverse',
          }} />
        </div>

        <div data-scroll-owner="true" className="scroll-owner" style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '24px 20px',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))',
          position: 'relative', zIndex: 1,
        }}>
        <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20, margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #0d9488 0%, #7c3aed 50%, #ec4899 100%)',
            padding: 4,
            boxShadow: '0 12px 40px rgba(13,148,136,0.3), 0 4px 16px rgba(124,58,237,0.2)',
            animation: 'pulse 3s ease-in-out infinite',
          }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: 16,
              background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src="/sitepe-logo.png" alt="SitePe" style={{ width: 48, height: 48, borderRadius: 12 }} 
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: 48, height: 48 }}>
                <Building2 style={{ width: 28, height: 28, color: '#0d9488' }} />
              </div>
            </div>
          </div>
          
          <h1 style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', margin: '0 0 4px', letterSpacing: '-0.02em' }}>
            <span>site</span>
            <span style={{ background: 'linear-gradient(135deg, #0d9488, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>pe</span>
          </h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, fontWeight: 500 }}>Construction ERP</p>
        </div>

        {/* Login Card */}
        <form onSubmit={handleSubmit} data-testid="login-form" style={{
          width: '100%',
          maxWidth: 340,
          background: 'rgba(255,255,255,0.85)',
          borderRadius: 24,
          padding: '28px 24px',
          border: '1px solid rgba(255,255,255,0.8)',
          boxShadow: '0 8px 32px rgba(13,148,136,0.1), 0 2px 8px rgba(0,0,0,0.04)',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}>
          
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Sparkles style={{ width: 14, height: 14, color: '#0d9488' }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: '#0d9488', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Welcome Back</span>
              <Sparkles style={{ width: 14, height: 14, color: '#7c3aed' }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Sign In</h2>
          </div>

          {/* Company Code */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Company Code</label>
            <div style={{ position: 'relative' }}>
              <Building2 style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'code' ? '#0d9488' : '#94a3b8',
                transition: 'color 0.2s',
              }} />
              <Input
                type="text"
                placeholder="e.g. KK5524"
                value={formData.companyCode}
                onChange={e => setFormData({ ...formData, companyCode: companyCodeInput(e.target.value) })}
                onPaste={e => { e.preventDefault(); setFormData({ ...formData, companyCode: companyCodeInput(e.clipboardData.getData('text')) }); }}
                onFocus={() => setFocusedField('code')}
                onBlur={() => setFocusedField(null)}
                data-testid="login-company-code"
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 16, fontWeight: 700, letterSpacing: '0.15em',
                  background: focusedField === 'code' ? '#fff' : '#f8fafc',
                  border: focusedField === 'code' ? '2px solid #0d9488' : '2px solid #e2e8f0',
                  borderRadius: 14, color: '#0f172a', outline: 'none', transition: 'all 0.2s',
                  textTransform: 'uppercase',
                }}
              />
            </div>
          </div>

          {/* Phone */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Phone Number</label>
            <div style={{ position: 'relative' }}>
              <Phone style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'phone' ? '#7c3aed' : '#94a3b8',
                transition: 'color 0.2s',
              }} />
              <Input
                type="tel"
                placeholder="Your registered phone"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                data-testid="login-phone"
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 15, fontWeight: 500,
                  background: focusedField === 'phone' ? '#fff' : '#f8fafc',
                  border: focusedField === 'phone' ? '2px solid #7c3aed' : '2px solid #e2e8f0',
                  borderRadius: 14, color: '#0f172a', outline: 'none', transition: 'all 0.2s',
                }}
              />
            </div>
          </div>

          {/* Submit */}
          <AuthError message={errorMessage} testId="login-error" />
          <button type="submit" disabled={loading} data-testid="login-submit"
            style={{
              width: '100%', height: 54, borderRadius: 14, border: 'none',
              background: loading ? 'rgba(13,148,136,0.5)' : 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
              color: '#fff', fontSize: 15, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: loading ? 'none' : '0 8px 24px rgba(13,148,136,0.35)',
              transition: 'all 0.2s', transform: 'translateZ(0)',
            }}>
            {loading ? <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} />
              : <>Sign In <ArrowRight style={{ width: 18, height: 18 }} /></>}
          </button>
        </form>

        {/* CTA Cards */}
        <div style={{
          width: '100%', maxWidth: 340, marginTop: 20,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
          position: 'relative', zIndex: 1,
        }}>
          <Link data-testid="login-create-company" to="/signup/company" style={{ textDecoration: 'none' }}>
            <div style={{
              borderRadius: 18, padding: '18px 16px', textAlign: 'center',
              background: `
                linear-gradient(175deg, rgba(255,255,255,0.18) 0%, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%, rgba(255,255,255,0.12) 100%),
                repeating-linear-gradient(90deg, rgba(255,255,255,0) 0px, rgba(255,255,255,0.06) 2px, rgba(255,255,255,0) 4px),
                linear-gradient(160deg, #dda8a0 0%, #c88a82 25%, #b06860 50%, #c88a82 75%, #e8c0b8 100%)
              `,
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 8px 24px rgba(180,110,100,0.35), 0 2px 8px rgba(200,138,130,0.25), inset 0 1px 0 rgba(255,220,210,0.4)',
              transition: 'all 0.2s',
            }}>
              <div style={{
                width: 30, height: 22, borderRadius: 5, margin: '0 auto 10px',
                background: 'linear-gradient(135deg, #c88a82, #dda8a0 40%, #c88a82 70%, #b06860)',
                border: '1px solid rgba(80,30,20,0.25)',
              }} />
              <p style={{ fontSize: 13, fontWeight: 800, color: '#2a0e0a', margin: '0 0 4px', textShadow: '0 1px 1px rgba(240,180,170,0.4)' }}>Create Company</p>
              <p style={{ fontSize: 10, color: '#5a1a10', margin: 0, opacity: 0.7 }}>Start free today</p>
            </div>
          </Link>
          <Link data-testid="login-join-team" to="/signup/employee" style={{ textDecoration: 'none' }}>
            <div style={{
              borderRadius: 18, padding: '18px 16px', textAlign: 'center',
              background: 'rgba(255,255,255,0.8)',
              border: '1px solid rgba(13,148,136,0.15)',
              boxShadow: '0 4px 16px rgba(13,148,136,0.08)',
              backdropFilter: 'blur(10px)', transition: 'all 0.2s',
            }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>👷</div>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>Join Team</p>
              <p style={{ fontSize: 10, color: '#64748b', margin: 0 }}>Have an invite?</p>
            </div>
          </Link>
        </div>

        {/* Footer Trust */}
        <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
          {['🔒 Secure', '⚡ Fast', '📊 Powerful'].map((item, i) => (
            <span key={i} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{item}</span>
          ))}
        </div>
        </div>{/* close centering wrapper */}
        </div>{/* close scroll-owner */}

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes float { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-20px) scale(1.05); } }
          @keyframes pulse { 0%, 100% { box-shadow: 0 12px 40px rgba(13,148,136,0.3); } 50% { box-shadow: 0 16px 48px rgba(13,148,136,0.4); } }
          input::placeholder { color: #94a3b8 !important; }
        `}</style>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT (2-Column Expanded)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div data-testid="login-page" style={{
      height: '100vh',
      display: 'flex', flexDirection: 'row',
      fontFamily: "'Manrope','Inter',sans-serif",
      background: 'linear-gradient(150deg,#f0fdfa 0%,#fdf4ff 35%,#fff7ed 65%,#f0fdf9 100%)',
      position: 'relative', overflow: 'hidden',
    }}>

      {/* ═══ GOLDEN SHINING SCROLLERS — BOTH SIDES ═══ */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, zIndex: 10, pointerEvents: 'none',
        background: 'linear-gradient(180deg, transparent 0%, #d4a574 15%, #f5d4a1 30%, #ffecd2 50%, #f5d4a1 70%, #d4a574 85%, transparent 100%)',
        boxShadow: '2px 0 20px rgba(212,165,116,0.5), 4px 0 40px rgba(245,212,161,0.3)',
        animation: 'goldenPulse 3s ease-in-out infinite',
      }}>
        <div style={{ position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.6) 30%, rgba(255,236,210,0.8) 50%, rgba(255,255,255,0.6) 70%, transparent)',
          animation: 'goldenShimmer 2s ease-in-out infinite',
        }}/>
      </div>
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, zIndex: 10, pointerEvents: 'none',
        background: 'linear-gradient(180deg, transparent 0%, #d4a574 15%, #f5d4a1 30%, #ffecd2 50%, #f5d4a1 70%, #d4a574 85%, transparent 100%)',
        boxShadow: '-2px 0 20px rgba(212,165,116,0.5), -4px 0 40px rgba(245,212,161,0.3)',
        animation: 'goldenPulse 3s ease-in-out infinite 0.5s',
      }}>
        <div style={{ position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.6) 30%, rgba(255,236,210,0.8) 50%, rgba(255,255,255,0.6) 70%, transparent)',
          animation: 'goldenShimmer 2s ease-in-out infinite 1s',
        }}/>
      </div>

      {/* Jali Pattern */}
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0, opacity: 0.045 }}>
        <defs>
          <pattern id="jali2" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <circle cx="30" cy="30" r="12" fill="none" stroke="#0d9488" strokeWidth="0.7"/>
            <circle cx="30" cy="30" r="6" fill="none" stroke="#0d9488" strokeWidth="0.4"/>
            <line x1="18" y1="30" x2="42" y2="30" stroke="#0d9488" strokeWidth="0.4"/>
            <line x1="30" y1="18" x2="30" y2="42" stroke="#0d9488" strokeWidth="0.4"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#jali2)"/>
      </svg>

      {/* Aurora effects */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 700, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(13,148,136,0.09) 0%,transparent 65%)', top: -200, left: -150 }}/>
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(124,58,237,0.07) 0%,transparent 65%)', top: -100, right: 0, maxWidth: '100%' }}/>
        <div style={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(236,72,153,0.05) 0%,transparent 65%)', top: '30%', left: '35%' }}/>
      </div>

      {/* ═══ LEFT — Brand Statement ═══ */}
      <div style={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '56px 60px', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 44 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899)', padding: 3,
            boxShadow: '0 8px 24px rgba(13,148,136,0.3),0 2px 8px rgba(124,58,237,0.2)',
          }}>
            <img src="/sitepe-logo.png" alt="SitePe" style={{ width: '100%', height: '100%', borderRadius: 11, display: 'block' }} />
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>
              <span style={{ color: '#0f172a' }}>site</span>
              <span style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>pe</span>
            </div>
            <div style={{ fontSize: 9, letterSpacing: '0.2em', color: 'rgba(13,148,136,0.7)', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>
              Construction ERP
            </div>
          </div>
        </div>

        {/* Royal badge */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 20,
          background: 'linear-gradient(135deg,rgba(13,148,136,0.08),rgba(124,58,237,0.06))',
          border: '1px solid rgba(13,148,136,0.2)', marginBottom: 20, width: 'fit-content' }}>
          <span style={{ fontSize: 12 }}>👑</span>
          <span style={{ fontSize: 10, fontWeight: 800,
            background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            letterSpacing: '0.1em' }}>TRUSTED BY 500+ COMPANIES</span>
        </div>

        {/* Headline */}
        <h1 style={{ fontSize: 52, fontWeight: 900, color: '#0f172a', margin: '0 0 16px', lineHeight: 1.06, letterSpacing: '-0.03em' }}>
          Build Your<br/>
          <span style={{ background: 'linear-gradient(135deg,#0d9488 0%,#7c3aed 35%,#ec4899 65%,#f97316 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>
            Construction Empire
          </span>
        </h1>
        <p style={{ fontSize: 15, color: '#64748b', margin: '0 0 30px', lineHeight: 1.7, maxWidth: 420, fontWeight: 400 }}>
          The complete construction management platform. Sites, bills, inventory & ledgers — all under one magnificent roof.
        </p>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 30, borderRadius: 14, overflow: 'hidden',
          border: '1px solid rgba(13,148,136,0.12)', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)' }}>
          {[{ n: '500+', l: 'Companies' }, { n: '50K+', l: 'Records' }, { n: '100+', l: 'Reports' }, { n: '99.9%', l: 'Uptime' }].map((s, i) => (
            <div key={i} style={{ flex: 1, padding: '13px 0', textAlign: 'center',
              borderRight: i < 3 ? '1px solid rgba(13,148,136,0.1)' : 'none' }}>
              <p style={{ fontSize: 19, fontWeight: 900, margin: 0, letterSpacing: '-0.02em',
                background: `linear-gradient(135deg,${['#0d9488', '#7c3aed', '#ec4899', '#f97316'][i]},${['#14b8a6', '#a855f7', '#f472b6', '#fb923c'][i]})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{s.n}</p>
              <p style={{ fontSize: 8, color: '#94a3b8', margin: '3px 0 0', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.l}</p>
            </div>
          ))}
        </div>

        {/* Features */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 36 }}>
          {['Real-time inventory tracking across all sites',
            'Complete bill, challan & payment management',
            '100+ powerful reports & dashboards',
            'Bank-grade security with role-based access'].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg,${['rgba(13,148,136,0.12)', 'rgba(124,58,237,0.1)', 'rgba(236,72,153,0.1)', 'rgba(249,115,22,0.1)'][i]},rgba(255,255,255,0.5))`,
                border: `1px solid ${['rgba(13,148,136,0.3)', 'rgba(124,58,237,0.25)', 'rgba(236,72,153,0.25)', 'rgba(249,115,22,0.25)'][i]}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle style={{ width: 9, height: 9, color: ['#0d9488', '#7c3aed', '#ec4899', '#f97316'][i] }} />
              </div>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ RIGHT — Login + CTA cards ═══ */}
      <div data-scroll-owner="true" className="scroll-owner" style={{ flex: '0 0 45%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 52px', position: 'relative', zIndex: 1, overflowY: 'auto', minHeight: 0 }}>

        {/* Divider */}
        <div style={{ position: 'absolute', left: 0, top: '10%', height: '80%', width: 1,
          background: 'linear-gradient(to bottom,transparent,rgba(13,148,136,0.2),rgba(124,58,237,0.15),transparent)' }}/>

        <div style={{ width: '100%', maxWidth: 380, position: 'relative', zIndex: 2 }}>

          {/* Form header */}
          <div style={{ marginBottom: 22, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to right,transparent,rgba(13,148,136,0.35))' }}/>
              <span style={{ fontSize: 13, background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>✦</span>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to left,transparent,rgba(124,58,237,0.3))' }}/>
            </div>
            <h2 style={{ fontSize: 25, fontWeight: 900, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Welcome Back</h2>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sign in to your command center</p>
          </div>

          {/* Login Card */}
          <form onSubmit={handleSubmit} data-testid="login-form" style={{
            background: 'rgba(255,255,255,0.75)', borderRadius: 22, padding: '26px 24px',
            border: '1.5px solid rgba(13,148,136,0.12)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.03),0 20px 48px rgba(13,148,136,0.08)',
            backdropFilter: 'blur(20px)', marginBottom: 14,
          }}>
            {/* Company code */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 9, fontWeight: 800, color: 'rgba(13,148,136,0.7)', letterSpacing: '0.14em',
                textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Company Code</label>
              <div style={{ position: 'relative' }}>
                <Building2 style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 15, height: 15, color: focusedField === 'code' ? '#0d9488' : '#cbd5e1', transition: 'color 0.2s' }}/>
                <Input type="text" placeholder="e.g. KK5524"
                  value={formData.companyCode}
                  onChange={e => setFormData({ ...formData, companyCode: companyCodeInput(e.target.value) })}
                  onPaste={e => { e.preventDefault(); setFormData({ ...formData, companyCode: companyCodeInput(e.clipboardData.getData('text')) }); }}
                  onFocus={() => setFocusedField('code')} onBlur={() => setFocusedField(null)}
                  required data-testid="login-company-code"
                  style={{
                    width: '100%', height: 44, paddingLeft: 38, fontSize: 17, fontWeight: 700, letterSpacing: '0.25em',
                    background: '#fff', textTransform: 'uppercase',
                    border: focusedField === 'code' ? '1.5px solid #0d9488' : '1.5px solid #e2e8f0',
                    borderRadius: 11, color: '#0f172a', outline: 'none',
                    boxShadow: focusedField === 'code' ? '0 0 0 3px rgba(13,148,136,0.1)' : 'none',
                    transition: 'all 0.2s',
                  }}/>
              </div>
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 22 }}>
              <label style={{ fontSize: 9, fontWeight: 800, color: 'rgba(124,58,237,0.7)', letterSpacing: '0.14em',
                textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Phone Number</label>
              <div style={{ position: 'relative' }}>
                <Phone style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 15, height: 15, color: focusedField === 'phone' ? '#7c3aed' : '#cbd5e1', transition: 'color 0.2s' }}/>
                <Input type="tel" placeholder="Your registered phone"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)}
                  required data-testid="login-phone"
                  style={{
                    width: '100%', height: 44, paddingLeft: 38, fontSize: 14, fontWeight: 500,
                    background: '#fff',
                    border: focusedField === 'phone' ? '1.5px solid #7c3aed' : '1.5px solid #e2e8f0',
                    borderRadius: 11, color: '#0f172a', outline: 'none',
                    boxShadow: focusedField === 'phone' ? '0 0 0 3px rgba(124,58,237,0.1)' : 'none',
                    transition: 'all 0.2s',
                  }}/>
              </div>
            </div>

            {/* Sign In */}
            <AuthError message={errorMessage} testId="login-error" />
            <button type="submit" disabled={loading} data-testid="login-submit"
              style={{
                width: '100%', height: 48, borderRadius: 13, border: 'none',
                background: loading ? 'rgba(13,148,136,0.5)' : 'linear-gradient(135deg,#0d9488 0%,#7c3aed 50%,#ec4899 100%)',
                color: '#fff', fontSize: 14, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(13,148,136,0.25)',
                transition: 'all 0.2s', letterSpacing: '0.01em',
              }}>
              {loading ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <>Sign In <ArrowRight style={{ width: 15, height: 15 }} /></>}
            </button>
          </form>

          {/* CTA Cards */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ height: 1, width: 16, background: 'linear-gradient(to right,transparent,rgba(13,148,136,0.3))' }}/>
            <span style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.14em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>New to SitePe?</span>
            <div style={{ height: 1, flex: 1, background: 'linear-gradient(to right,rgba(13,148,136,0.3),transparent)' }}/>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {/* Create Company — Rose Gold Metallic */}
            <Link data-testid="login-create-company" to="/signup/company" style={{ textDecoration: 'none' }}>
              <div style={{
                borderRadius: 16, padding: '16px 14px',
                background: `
                  linear-gradient(175deg, rgba(255,255,255,0.18) 0%, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%, rgba(255,255,255,0.12) 100%),
                  repeating-linear-gradient(90deg, rgba(255,255,255,0) 0px, rgba(255,255,255,0.06) 2px, rgba(255,255,255,0) 4px),
                  linear-gradient(160deg, #dda8a0 0%, #c88a82 25%, #b06860 50%, #c88a82 75%, #e8c0b8 100%)
                `,
                boxShadow: '0 10px 28px rgba(180,110,100,0.40), 0 4px 12px rgba(200,138,130,0.30), inset 0 1px 0 rgba(255,220,210,0.5)',
                border: '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden', minHeight: 140,
              }}>
                <div style={{ position: 'absolute', top: 0, left: '8%', width: '18%', height: '100%',
                  background: 'linear-gradient(180deg,rgba(255,220,210,0.30),rgba(240,180,170,0.12),transparent)', pointerEvents: 'none' }}/>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: 38, height: 28, borderRadius: 6, marginBottom: 10,
                    background: 'linear-gradient(135deg, #c88a82, #dda8a0 40%, #c88a82 70%, #b06860)',
                    border: '1px solid rgba(80,30,20,0.25)',
                  }} />
                  <p style={{ fontSize: 13, fontWeight: 900, color: '#2a0e0a', margin: '0 0 3px', textShadow: '0 1px 0 rgba(240,180,170,0.4)' }}>Create Company</p>
                  <p style={{ fontSize: 9, color: 'rgba(42,14,10,0.6)', margin: '0 0 12px', lineHeight: 1.4, fontWeight: 600 }}>Launch your empire free</p>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20,
                    background: 'rgba(42,14,10,0.15)', border: '1px solid rgba(42,14,10,0.2)', fontSize: 10, fontWeight: 800, color: '#2a0e0a' }}>
                    Get Started <ArrowRight style={{ width: 10, height: 10 }} />
                  </div>
                </div>
              </div>
            </Link>

            {/* Join Team */}
            <Link data-testid="login-join-team" to="/signup/employee" style={{ textDecoration: 'none' }}>
              <div style={{
                borderRadius: 16, padding: '16px 14px',
                background: 'rgba(255,255,255,0.7)', border: '1.5px solid rgba(13,148,136,0.15)',
                backdropFilter: 'blur(16px)', boxShadow: '0 6px 20px rgba(13,148,136,0.07)',
                cursor: 'pointer', transition: 'all 0.2s',
              }}>
                <div style={{ fontSize: 22, marginBottom: 7 }}>👷</div>
                <p style={{ fontSize: 12, fontWeight: 900, color: '#0f172a', margin: '0 0 3px' }}>Join Team</p>
                <p style={{ fontSize: 9, color: '#94a3b8', margin: '0 0 10px', lineHeight: 1.4 }}>Have a code? Join now</p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20,
                  background: 'linear-gradient(135deg,#0d9488,#7c3aed)', fontSize: 10, fontWeight: 800, color: '#fff' }}>
                  Join Now <ArrowRight style={{ width: 10, height: 10 }} />
                </div>
              </div>
            </Link>
          </div>

          {/* Trust badges */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 16 }}>
            {[{ icon: Shield, l: 'Secure', c: '#0d9488' }, { icon: Zap, l: 'Fast', c: '#7c3aed' }, { icon: BarChart3, l: 'Powerful', c: '#ec4899' }].map(({ icon: Icon, l, c }, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon style={{ width: 11, height: 11, color: c, opacity: 0.5 }} />
                <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes goldenPulse { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
        @keyframes goldenShimmer { 0% { transform: translateY(100%); } 100% { transform: translateY(-100%); } }
        input::placeholder { color: #cbd5e1 !important; }
      `}</style>
    </div>
  );
};

export default LoginPage;
